import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const VehiclesAwaiting = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectId = localStorage.getItem('selectedProject');
        const supplierName = sessionStorage.getItem('selectedSupplierTab');

        if (!projectId || !supplierName) {
          throw new Error('Missing projectId or Supplier Name in storage');
        }

        const response = await axios.post(`${baseURL}get_packing_list_table`, {
          projectId,
          supplierName,
        });

        if (response.data.status === 'error') {
          throw new Error(response.data.message || 'Failed to fetch data');
        }

        setTableData(
          response.data.data.map((item, index) => ({
            srNo: index + 1,
            orderNo: item.order_no || 'N/A',
            vehicleNo: item.vehicle_no || 'N/A',
            vehicleType: item.vehicle_type || 'Unknown',
            dateOfShipping: item.date_of_shipping || 'N/A',
            moduleType: item.module_type || 'N/A',
            noOfPallets: item.no_of_pallets || 0,
            packingList: item.packing_list_link || 'N/A',
            status: item.status || 'In Transit',
          }))
        );
        setLoading(false);
      } catch (err) {
        if (err.response && err.response.data && err.response.data.message) {
          setError(err.response.data.message);
        } else {
          setError(err.message || 'An unexpected error occurred');
        }
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleStatusToggle = async (row) => {
    try {
      const projectId = localStorage.getItem('selectedProject');
      if (!projectId) {
        throw new Error('Missing projectId in storage');
      }

      // Build payload conditionally based on vehicle type
      const payload = {
        projectId,
        date_of_loading: row.dateOfShipping,
        status: 'Delivered',
      };

      const vehicleTypeLower = row.vehicleType.toLowerCase().trim();

      if (vehicleTypeLower === 'truck') {
        payload.truck_no = row.vehicleNo;
      } else if (vehicleTypeLower === 'container') {
        payload.container_no = row.vehicleNo;
      } else {
        throw new Error(`Cannot update status: Unknown vehicle type "${row.vehicleType}"`);
      }

      const response = await axios.post(`${baseURL}update_transit_status`, payload);

      if (response.data.status === 'error') {
        throw new Error(response.data.message || 'Failed to update status');
      }

      setTableData((prevData) =>
        prevData.map((item) =>
          item.srNo === row.srNo ? { ...item, status: 'Delivered' } : item
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleStartInspection = (row) => {
    sessionStorage.setItem('inspectionData', JSON.stringify(row));
    navigate('/vehicle-inspection');
  };

  const filteredData = tableData.filter((row) =>
    Object.values(row).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div className="bg-gray-100 p-6">
      <div className="w-full mx-auto">
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by Order No, Vehicle No, or Module Type..."
            className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {error && (
          <div className="mb-4 p-4 text-center text-red-500 bg-red-100 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500">Loading...</div>
          ) : (
            <table className="min-w-full table-auto border-collapse">
              <thead>
                <tr className="bg-gray-600 text-center text-sm font-semibold text-white">
                  <th className="p-4 whitespace-nowrap">Sr.no</th>
                  <th className="p-4 whitespace-nowrap">Order No</th>
                  <th className="p-4 whitespace-nowrap">Vehicle No</th>
                  <th className="p-4 whitespace-nowrap">Vehicle Type</th>
                  <th className="p-4 whitespace-nowrap">Date Of Loading</th>
                  <th className="p-4 whitespace-nowrap">Module Type</th>
                  <th className="p-4 whitespace-nowrap">No of Pallets</th>
                  <th className="p-4 whitespace-nowrap">Packing List</th>
                  <th className="p-4 whitespace-nowrap">Status</th>
                  <th className="p-4 whitespace-nowrap">Inspection</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((row) => (
                    <tr key={row.srNo} className="border-t text-sm text-center text-gray-700 hover:bg-gray-50">
                      <td className="p-4 whitespace-nowrap">{row.srNo}</td>
                      <td className="p-4 whitespace-nowrap">{row.orderNo}</td>
                      <td className="p-4 whitespace-nowrap">{row.vehicleNo}</td>
                      <td className="p-4 whitespace-nowrap">{row.vehicleType}</td>
                      <td className="p-4 whitespace-nowrap">{row.dateOfShipping}</td>
                      <td className="p-4 whitespace-nowrap">{row.moduleType}</td>
                      <td className="p-4 whitespace-nowrap">{row.noOfPallets}</td>
                      <td className="p-4 whitespace-nowrap">
                        {row.packingList !== 'N/A' ? (
                          <a
                            href={row.packingList}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline"
                          >
                            LINK
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        <button
                          onClick={() => handleStatusToggle(row)}
                          disabled={row.status === 'Delivered'}
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            row.status === 'In Transit'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-green-100 text-green-700'
                          } ${row.status === 'Delivered' ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          {row.status}
                        </button>
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        {row.status === 'Delivered' && (
                          <button
                            onClick={() => handleStartInspection(row)}
                            className="px-3 py-1 rounded-full text-xs font-medium transition-colors bg-gray-500 hover:bg-gray-600 text-white"
                          >
                            Start
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="p-4 text-center text-gray-500">
                      No matching records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehiclesAwaiting;